// src/pages/ConsultantForm.jsx   (or rename to CreateConsultant.jsx if preferred)
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import SummaryApi from '../common/index';

const ConsultantForm = () => {
  const { id } = useParams(); // base64-encoded admin_id from URL (for edit)
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    admin_id: '',
    full_name: '',
    mobile_number1: '',
    mobile_number2: '',
    user_name: '',
    password: '',
    consultancy_name: '',
    address: '',
    city: '',
    member_type: 'Consultancy', // always sent in payload — not shown in UI
    is_active: '1',
  });

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const nameInputRef = useRef(null);

  // Fetch data only in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchConsultant = async () => {
        try {
          let decodedAdminId;
          try {
            decodedAdminId = atob(id);
            if (!decodedAdminId || isNaN(decodedAdminId)) {
              throw new Error('Invalid consultant ID format');
            }
          } catch (decodeErr) {
            throw new Error('Invalid or corrupted consultant ID in URL');
          }

          const res = await fetch(SummaryApi.GetconsultantByid?.url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ admin_id: decodedAdminId }),
          });

          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }

          const result = await res.json();
          if (result.status === true && result.data?.length > 0) {
            const data = result.data[0];
            setFormData({
              admin_id: data.admin_id || decodedAdminId,
              full_name: data.full_name || '',
              mobile_number1: data.mobile_number1 || '',
              mobile_number2: data.mobile_number2 || '',
              user_name: data.user_name || '',
              password: data.password || '',
              consultancy_name: data.consultancy_name || '',
              address: data.address || '',
              city: data.city || '',
              member_type: data.member_type || 'Consultancy', // still received & kept
              is_active: data.is_active?.toString() || '1',
            });
          } else {
            navigate('/consultant-report');
          }
        } catch (err) {

          navigate('/consultant-report');
        } finally {
          setLoading(false);
        }
      };
      fetchConsultant();
    }
  }, [id, isEditMode, navigate]);

  // Auto-focus name field
  useEffect(() => {
    if (!loading && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [loading]);

  // Real-time validation → button enable/disable
  useEffect(() => {
    const isValid =
      formData.full_name?.trim().length >= 3 &&
      /^\d{10}$/.test(formData.mobile_number1?.trim() || '') &&
      (!formData.mobile_number2?.trim() || /^\d{10}$/.test(formData.mobile_number2.trim())) &&
      formData.consultancy_name?.trim().length >= 3 &&
      formData.city?.trim().length >= 1 &&
      formData.member_type?.trim().length > 0 &&           // safety check (always true)
      (isEditMode
        ? true
        : formData.user_name?.trim().length >= 4 &&
          formData.password?.trim().length >= 6);

    setIsFormValid(isValid);
  }, [formData, isEditMode]);

const handleChange = (e) => {
  const { name, value, type, checked } = e.target;

  if (type === 'checkbox') {
    setFormData((prev) => ({ ...prev, [name]: checked ? '1' : '0' }));
    return;
  }

  let updatedValue = value;

  // ✅ Only letters and spaces for Full Name, Consultancy Name, City
  if (name === 'full_name' || name === 'consultancy_name' || name === 'city') {
    updatedValue = value.replace(/[^a-zA-Z\s]/g, '');
  }

  // ✅ Mobile numbers → only digits
  if (name === 'mobile_number1' || name === 'mobile_number2') {
    updatedValue = value.replace(/\D/g, '').slice(0, 10);
  }

  setFormData((prev) => ({
    ...prev,
    [name]: updatedValue,
  }));
};
  const validateForm = () => {
    if (!formData.full_name?.trim() || formData.full_name.trim().length < 3) {
      toast.error('Full Name is required (min 3 characters)');
      return false;
    }
    if (!/^\d{10}$/.test(formData.mobile_number1?.trim() || '')) {
      toast.error('Mobile Number 1 must be exactly 10 digits');
      return false;
    }
    if (formData.mobile_number2 && !/^\d{10}$/.test(formData.mobile_number2.trim())) {
      toast.error('Mobile Number 2 must be exactly 10 digits');
      return false;
    }
    if (!formData.consultancy_name?.trim() || formData.consultancy_name.trim().length < 3) {
      toast.error('Consultancy Name is required (min 3 characters)');
      return false;
    }
    if (!formData.city?.trim()) {
      toast.error('City is required');
      return false;
    }
    // member_type is not checked in UI → we assume it's always 'Consultancy'
    if (!isEditMode) {
      if (!formData.user_name?.trim() || formData.user_name.trim().length < 4) {
        toast.error('Username must be at least 4 characters');
        return false;
      }
      if (!formData.password?.trim() || formData.password.trim().length < 6) {
        toast.error('Password must be at least 6 characters');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = { ...formData };           // ← member_type is included here
      if (!isEditMode) {
        delete payload.admin_id;
      } else {
        if (!payload.password?.trim()) {
          delete payload.password;
        }
      }

      const api = isEditMode ? SummaryApi.UpdateConsultant : SummaryApi.createConsultant;

      const res = await fetch(api.url, {
        method: api.method || 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.status === true || result.success) {
        toast.success(isEditMode ? 'Consultant updated successfully!' : 'Consultant created successfully!');
        setTimeout(() => {
          navigate('/consultant-report');
        }, 1200);
      } else {
        toast.error(result.message || (isEditMode ? 'Update failed' : 'Creation failed'));
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Network error. Please check connection.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center text-gray-600">
        Loading consultant details...
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow rounded-lg">
      <h2 className="text-2xl font-bold mb-6 text-center text-blue-900">
        {isEditMode ? 'Update Consultant' : 'Create New Consultant'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-10">
        {/* Basic Information */}
        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b border-gray-300 pb-2">
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-600">*</span>
              </label>
  <input
  ref={nameInputRef}
  type="text"
  name="full_name"
  value={formData.full_name}
  onChange={handleChange}
  placeholder="Enter full name"
  className="block w-full border border-gray-300 rounded-md p-2"
  required
/>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Consultancy Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="consultancy_name"
                value={formData.consultancy_name}
                onChange={handleChange}
                placeholder="Name of the consultancy"
                className="block w-full border border-gray-300 rounded-md p-2"
                required
              />
            </div>

            {/* Member Type field completely removed from UI */}
          </div>
        </div>

        {/* Contact Details */}
        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b border-gray-300 pb-2">
            Contact Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile Number 1 <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                name="mobile_number1"
                value={formData.mobile_number1}
                onChange={handleChange}
                placeholder="10-digit mobile number"
                maxLength={10}
                className="block w-full border border-gray-300 rounded-md p-2"
                required                 disabled={isEditMode}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alt Mobile Number
              </label>
              <input
                type="tel"
                name="mobile_number2"
                value={formData.mobile_number2}
                onChange={handleChange}
                placeholder="Optional second number"
                maxLength={10}
                className="block w-full border border-gray-300 rounded-md p-2"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={2}
                placeholder="Full address"
                className="block w-full border border-gray-300 rounded-md p-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="e.g. Vijayawada"
                className="block w-full border border-gray-300 rounded-md p-2"
                required
              />
            </div>
          </div>
        </div>

        {/* Login Details */}
        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b border-gray-300 pb-2">
            Login Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="user_name"
                value={formData.user_name}
                onChange={handleChange}
                placeholder="Login username"
                className="block w-full border border-gray-300 rounded-md p-2"
                required
                disabled={isEditMode}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isEditMode ? 'New Password (leave blank if not changing)' : 'Password'}{' '}
                <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={isEditMode ? 'Enter new password if changing' : 'Minimum 6 characters'}
                className="block w-full border border-gray-300 rounded-md p-2 font-mono"
                required={!isEditMode}
              />
            </div>
          </div>
        </div>

        {/* Status - only in edit mode */}
        {isEditMode && (
          <div>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b border-gray-300 pb-2">
              Status
            </h3>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Is Active</label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active === '1'}
                  onChange={handleChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
              <span
                className={`text-sm font-medium ${
                  formData.is_active === '1' ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {formData.is_active === '1' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-center mt-10 gap-6">
          <button
            type="submit"
            disabled={submitting || !isFormValid}
            className={`px-12 py-3 rounded-md font-semibold text-white transition-all ${
              submitting || !isFormValid
                ? 'bg-gray-400 cursor-not-allowed'
                : isEditMode
                ? 'bg-blue-700 hover:bg-blue-800'
                : 'bg-green-700 hover:bg-green-800'
            }`}
          >
            {submitting
              ? 'Saving...'
              : isEditMode
              ? 'Update Consultant'
              : 'Create Consultant'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConsultantForm;